import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { buscarPlaceDetails, buscarPlaces, requestService } from '../Utils/Services';

/**
 * Interface para sugestão de endereço
 */
export interface SugestaoEndereco {
    placeId: string;
    descricao: string;
    descricaoPrincipal: string;
    descricaoSecundaria: string;
    tipos: string[];
}

/**
 * Interface para detalhes do endereço
 */
export interface DetalhesEndereco {
    endereco: string;
    latitude: string;
    longitude: string;
    distanciaKm?: number;
    taxaEntrega?: string;
    dentroAreaEntrega: boolean;
}

/**
 * Interface para configuração de entrega
 */
interface ConfiguracaoEntrega {
    tipo?: string;
    raio?: string;
    taxa?: string | number;
}

/**
 * Props do hook
 */
interface UseGooglePlacesSearchProps {
    loja: string;
    configuracoesEntrega?: ConfiguracaoEntrega[];
    onError?: (mensagem: string) => void;
    onLoading?: (loading: boolean) => void;
    debounceTime?: number; // Tempo de espera antes de buscar (padrão: 2000ms)
    minChars?: number; // Mínimo de caracteres para buscar (padrão: 4)
}

/**
 * Custom Hook para busca de endereços usando Google Places API
 * 
 * @example
 * ```tsx
 * const { 
 *   listaEnderecos, 
 *   buscandoEndereco,
 *   buscarSugestoes,
 *   buscarDetalhes,
 *   limparSugestoes
 * } = useGooglePlacesSearch({
 *   loja: empresaId,
 *   configuracoesEntrega: dadosEmpresa.configuracoesEntrega,
 *   onError: (msg) => setTextToast(msg),
 *   onLoading: setShowLoading
 * });
 * ```
 */
export const useGooglePlacesSearch = ({
    loja,
    configuracoesEntrega = [],
    onError,
    onLoading,
    debounceTime = 2000,
    minChars = 4
}: UseGooglePlacesSearchProps) => {
    const history = useHistory();

    // Estados
    const [listaEnderecos, setListaEnderecos] = useState<SugestaoEndereco[]>([]);
    const [buscandoEndereco, setBuscandoEndereco] = useState(false);
    const [ultimaBuscaRealizada, setUltimaBuscaRealizada] = useState('');

    // Ref para timeout de debounce
    const searchTimeout = useRef<any>(null);

    /**
     * Limpa a lista de sugestões
     */
    const limparSugestoes = useCallback(() => {
        setListaEnderecos([]);
        setUltimaBuscaRealizada('');
        setBuscandoEndereco(false);
    }, []);

    /**
     * Busca sugestões de endereço com debounce
     */
    const buscarSugestoes = useCallback((enderecoTexto: string, forceBusca: boolean = false) => {
        // Validações iniciais
        if (enderecoTexto.trim() === "") {
            limparSugestoes();
            return;
        }

        if (enderecoTexto.trim().length < minChars) {
            setListaEnderecos([]);
            setBuscandoEndereco(false);
            return;
        }

        // Evitar busca duplicada
        if (!forceBusca && enderecoTexto.trim() === ultimaBuscaRealizada) {
            return;
        }

        // Limpar timeout anterior
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Criar novo timeout com debounce
        searchTimeout.current = setTimeout(() => {
            const enderecoTratado = enderecoTexto.toLowerCase().replace(/\s/g, "%20");
            const empresaid = localStorage.getItem("empresaid") || loja || "";
            const urlCompleta = buscarPlaces.url
                .replace("{id}", empresaid.replace(/#/g, '%23'))
                .replace("{endereco}", enderecoTratado);

            setBuscandoEndereco(true);

            requestService(
                urlCompleta,
                {
                    method: buscarPlaces.method,
                    headers: [["token", window.localStorage.getItem("token")]],
                    credentials: 'include'
                },
                history,
                (response: any) => {
                    console.log("Resposta buscarPlaces:", response);

                    let sugestoes: SugestaoEndereco[] = [];

                    // Suporta dois formatos de resposta:
                    // 1. response.data.sugestoes (novo formato aninhado)
                    // 2. response.data (formato antigo direto)
                    const dadosSugestoes = response?.data?.sugestoes || response?.data;

                    if (dadosSugestoes && Array.isArray(dadosSugestoes)) {
                        sugestoes = dadosSugestoes.map((item: any) => ({
                            placeId: item.placeId || item.place_id,
                            descricao: item.descricao || item.description,
                            descricaoPrincipal: item.descricaoPrincipal || item.structured_formatting?.main_text || item.description || item.descricao,
                            descricaoSecundaria: item.descricaoSecundaria || item.structured_formatting?.secondary_text || '',
                            tipos: item.tipos || item.types || []
                        }));
                    }

                    if (sugestoes.length > 0) {
                        console.log("Sugestões processadas:", sugestoes);
                        setListaEnderecos(sugestoes);
                        setUltimaBuscaRealizada(enderecoTexto.trim());
                    } else {
                        console.log("Nenhuma sugestão encontrada");
                        setListaEnderecos([]);
                    }
                },
                (error: any) => {
                    console.log("Erro ao buscar sugestões:", error);
                    onError?.("Erro ao buscar endereços. Tente novamente.");
                }
            ).finally(() => {
                setBuscandoEndereco(false);
            });
        }, forceBusca ? 0 : debounceTime);
    }, [loja, ultimaBuscaRealizada, minChars, debounceTime, history, onError, limparSugestoes]);

    /**
     * Busca detalhes completos do endereço selecionado e calcula taxa de entrega
     */
    const buscarDetalhes = useCallback(
        (placeId: string, enderecoFormatado: string): Promise<DetalhesEndereco> => {
            return new Promise((resolve, reject) => {
                const empresaid = localStorage.getItem("empresaid") || loja || "";
                const urlCompleta = buscarPlaceDetails.url
                    .replace("{id}", empresaid.replace(/#/g, '%23'))
                    .replace("{placeId}", placeId);

                onLoading?.(true);

                requestService(
                    urlCompleta,
                    {
                        method: buscarPlaceDetails.method,
                        headers: [["token", window.localStorage.getItem("token")]],
                        credentials: 'include'
                    },
                    history,
                    (response: any) => {
                        console.log("Resposta completa buscarPlaceDetails:", response);

                        if (response && response.data) {
                            const responseData = response.data;

                            // Extrair latitude e longitude
                            // Suporta dois formatos:
                            // 1. Formato novo: response.data.latitude e response.data.longitude diretos
                            // 2. Formato antigo: response.data.geometry.location.lat/lng
                            const latitude = responseData.latitude || responseData.geometry?.location?.lat;
                            const longitude = responseData.longitude || responseData.geometry?.location?.lng;

                            console.log("Latitude extraída:", latitude);
                            console.log("Longitude extraída:", longitude);

                            if (latitude && longitude) {
                                // Extrair distância em km
                                // Formato novo: response.data.distancia.km
                                // Formato antigo: response.data.distanciaKm
                                const distanciaKm = responseData.distancia?.km ?? responseData.distanciaKm;
                                console.log("Distância extraída (km):", distanciaKm);

                                let taxaEntrega = "";
                                let dentroAreaEntrega = false;
                                let mensagemErro = "";

                                // Calcular taxa de entrega se distância disponível
                                if (distanciaKm !== undefined && distanciaKm !== null) {
                                    // Filtrar e ordenar configurações de raio
                                    const configuracoesRaio = configuracoesEntrega
                                        ?.filter((config: any) => config.tipo === "raio" || !config.tipo) || [];

                                    configuracoesRaio.sort((a: any, b: any) =>
                                        parseFloat(a.raio || "0") - parseFloat(b.raio || "0")
                                    );

                                    console.log("Configurações de raio ordenadas:", configuracoesRaio);

                                    // Buscar primeira configuração válida
                                    const configEntrega = configuracoesRaio.find((config: any) => {
                                        const raioKm = parseFloat(config.raio || "0");
                                        console.log(`Comparando: distância ${distanciaKm} <= raio ${raioKm}?`);
                                        return distanciaKm <= raioKm;
                                    });

                                    if (configEntrega) {
                                        console.log("Configuração de entrega encontrada:", configEntrega);
                                        taxaEntrega = configEntrega.taxa?.toString() || "0,00";
                                        dentroAreaEntrega = true;
                                    } else {
                                        console.log("Endereço fora da área de entrega. Distância:", distanciaKm);
                                        mensagemErro = "Fora da área de entrega.";
                                    }
                                } else {
                                    console.log("Distância não disponível na resposta");
                                    mensagemErro = "Endereço localizado, mas não foi possível calcular a taxa de entrega. Informe manualmente.";
                                }

                                const resultado: DetalhesEndereco = {
                                    endereco: enderecoFormatado,
                                    latitude: latitude.toString(),
                                    longitude: longitude.toString(),
                                    distanciaKm,
                                    taxaEntrega,
                                    dentroAreaEntrega
                                };

                                // Limpar sugestões após seleção
                                setListaEnderecos([]);

                                if (mensagemErro) {
                                    onError?.(mensagemErro);
                                }

                                resolve(resultado);
                            } else {
                                const erro = "Erro ao extrair coordenadas do endereço.";
                                console.error("Latitude ou longitude não encontradas na resposta");
                                onError?.(erro);
                                reject(new Error(erro));
                            }
                        } else {
                            const erro = "Resposta inválida da API";
                            onError?.(erro);
                            reject(new Error(erro));
                        }
                    },
                    (error: any) => {
                        console.log("Erro ao buscar detalhes:", error);
                        const mensagem = error.message || "Erro ao localizar endereço.";
                        onError?.(mensagem);
                        reject(new Error(mensagem));
                    }
                ).finally(() => {
                    onLoading?.(false);
                });
            });
        },
        [loja, configuracoesEntrega, history, onError, onLoading]
    );

    // Cleanup do timeout ao desmontar
    useState(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    });

    return {
        listaEnderecos,
        buscandoEndereco,
        buscarSugestoes,
        buscarDetalhes,
        limparSugestoes
    };
};
